import React, {useEffect} from 'react';
import {Grid, Skeleton} from '@mui/material';
import {IVendorCraftEntity, IVendorLicenseEntity, Scraper, Vendor} from 'pwm-shared/src/types';
import {IVendorDbaEntity} from 'pwm-shared/src/types/Vendor';
import {useTranslation} from 'react-i18next';
import {tokens} from 'src/PWM/locales/tokens';
import {useVendorApi} from 'src/PWM/pages/vendors/vendor.api';
import RegistrationCard from 'src/PWM/pages/vendors/components/RegistrationCard';
import {PWMCard, PWMCardContent, PWMCardHeader} from 'src/PWM/components/PWMCard';

const VendorValidations: React.FC<IVendorValidationsProps> = ({vendorId, data, setIsLoading, showConfirmationDialog}) => {
  const [validations, setValidations] = React.useState<Vendor.GetVendorValidations[]>([]);
  const [hasRun, setHasRun] = React.useState<boolean>(false);

  const {t} = useTranslation();
  const vendorApi = useVendorApi();

  useEffect(() => {
    getData();
  },[vendorId]);

  const getData = async () => {
    if(!vendorApi || !vendorId) return;
    const {data} = await vendorApi.getVendorValidations(vendorId);
    console.dir(data.data);
    setValidations(data.data);
    setHasRun(true);
  }

  const {
    name,
    dirRegNo,
    licNo,
    crafts,
    dirRegId,
    dirRegFrom,
    dirRegTo,
    dirRegLastRefresh,
    dirRegNoVerified,
    dirRegActive,
    licFrom,
    licTo,
    licMessage,
    licLastRefresh,
    licActive,
    licNoVerified,
    licenses
  } = data;

  const revalidateDir = async () => {
    if (dirRegNo && data.id && vendorApi) {
      setIsLoading(true);
      try {
        await vendorApi.refreshDirInfo({dirRegNo, vendorId: data.id});
        showConfirmationDialog('DIR', dirRegNo);
      } catch (err) {
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    }
  };
  const revalidateCslb = async () => {
    if (licNo && data.id && vendorApi) {
      setIsLoading(true);
      try {
        await vendorApi.refreshCslbInfo({licNo, vendorId: data.id});
        showConfirmationDialog('CSLB', licNo);
      } catch (err) {
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    }
  };

  // display loading indicator
  if (!hasRun) {
    return <Skeleton variant="rectangular" width="100%" height="100%" animation="wave" sx={{p: 2}} />;
  }
  return (
    <PWMCard>
      <PWMCardHeader title={t(tokens.interface.vendorregistration)} />
      <PWMCardContent>
        <Grid container={true} spacing={2}>
          <Grid item={true} xs={12} md={6}>
            <RegistrationCard
              title={`${t(tokens.interface.dir)}`}
              vendor={name}
              setIsLoading={setIsLoading}
              idNo={dirRegNo}
              from={dirRegFrom}
              to={dirRegTo}
              lastVerified={dirRegLastRefresh!}
              verified={dirRegNoVerified}
              warning={!dirRegActive}
              refresh={revalidateDir}
              registrations={crafts?.map((c) => ({name: c.craft.name}))}
              validations={validations?.filter(({system}) => system === 'dir')}
              url={`https://services.dir.ca.gov/gsp?id=dir_public_form&table=x_cdoi2_csm_portal_customer_account_lookup&sys_id=${dirRegId}&view=public`}
            />
          </Grid>
          {licNo && (
            <Grid item={true} xs={12} md={6}>
              <RegistrationCard
                title={`${t(tokens.interface.cslb)}`}
                vendor={name}
                setIsLoading={setIsLoading}
                idNo={licNo}
                from={licFrom}
                to={licTo}
                message={licMessage}
                lastVerified={licLastRefresh!}
                warning={!licActive}
                verified={licNoVerified}
                refresh={revalidateCslb}
                registrations={licenses?.map((l) => ({name: l.license.name}))}
                validations={validations?.filter(({system}) => system === 'cslb')}
                url={`https://www.cslb.ca.gov/OnlineServices/CheckLicenseII/LicenseDetail.aspx?LicNum=${licNo}`}
              />
            </Grid>)}
        </Grid>
      </PWMCardContent>
    </PWMCard>
  );
};

export default VendorValidations;

interface IVendorValidationsProps {
  children?: React.ReactNode;
  vendorId: number;
  data: GetVendorResponse;
  setIsLoading(isLoading: boolean): any;
  showConfirmationDialog(message: string, regNo: string): any;
}

interface GetVendorValidationsResponse {
  vendorId: number;
  success: boolean;
  system: Scraper.DataSystem;
  message?: string;
  id: number;
  isActive: boolean;
  created: Date;
  createdBy?: number;
  updated: Date;
  updatedBy?: number;
  deleted: Date;
  deletedBy?: number;
}

interface GetVendorResponse {
  name: string;
  dirRegId?: string;
  dirRegNo: string;
  dirRegActive: boolean;
  dirRegFrom: Date;
  dirRegTo: Date;
  dirRegNoVerified: boolean;
  dirRegLastRefresh?: Date;
  sys_id?: string;
  licNo?: string;
  licActive: boolean;
  licMessage?: string;
  licFrom: Date;
  licTo: Date;
  licNoVerified: boolean;
  licLastRefresh?: Date;
  licenses: IVendorLicenseEntity[];
  crafts: IVendorCraftEntity[];
  dbaName: IVendorDbaEntity[];
  id: number;
  created: Date;
  createdBy?: number;
  updated: Date;
  updatedBy?: number;
  deleted: Date;
  deletedBy?: number;
}